import { ReactElement, useCallback, useMemo } from 'react';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { templatePath } from '@components/Hooks/useRouter';
import { usePriceCalculation } from '@components/Hooks/usePriceCalculation';
import TemplateCard, { TemplateCardProps } from '../TemplateCard';
import TemplateCardMobile from '../TemplateCardMobile';
import TemplateCardMobileSimple from '../TemplateCardMobileSimple';
import CardInfo from '../CardInfo/CardInfo';
import { LoadedTemplateCardProps } from '../TemplateLoadedCard/TemplateLoadedCard';

export interface TemplateCardAdaptiveProps
  extends Pick<
    TemplateCardProps,
    | 'actionButton'
    | 'actionButtonVisibility'
    | 'overlayContent'
    | 'forceRender'
    | 'template'
    | 'index'
    | 'loader'
  > {
  isMobileView?: boolean;
  isSimpleMobileView?: boolean;
  showShadow?: boolean;
  onCustomClick?: () => void;
  cardActions?: ReactElement;
  entryParam?: string;
}

export function buildTemplatePageLink(
  templateId?: string | null,
  entryParam?: string,
) {
  if (!templateId) {
    return '';
  }

  const base = `${templatePath}/${templateId}`;
  return entryParam ? `${base}?entry=${entryParam}` : base;
}

const TemplateCardAdaptive = (props: TemplateCardAdaptiveProps) => {
  const { t } = useTranslation();
  const { logBiClick } = useTemplatesBi({
    is_mobile: Boolean(props?.isMobileView || props?.isSimpleMobileView),
  });

  const onClickBIDesktop = useCallback(() => {
    logBiClick({
      click_type: 'Use template',
      click_data: { template_id: props?.template?.id },
    });
  }, [logBiClick, props?.template]);

  const templateHref = useMemo(
    () => buildTemplatePageLink(props?.template?.id, props?.entryParam),
    [props?.template?.id, props?.entryParam],
  );

  if (props.isSimpleMobileView) {
    return (
      <TemplateCardMobileSimple
        template={props.template}
        onCustomClick={props.onCustomClick}
        actionButton={props.actionButton}
      />
    );
  }

  if (props.isMobileView) {
    return <TemplateCardMobile {...props} entryParam={props.entryParam} />;
  }

  return (
    <TemplateCard
      {...props}
      t={t}
      onClick={props.onCustomClick || onClickBIDesktop}
      templateHref={templateHref}
      CardInfo={DesktopCardInfo}
    />
  );
};

const DesktopCardInfo: LoadedTemplateCardProps['CardInfo'] = ({
  template,
  hovered,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { getLocalizedTotalPrice } = usePriceCalculation();

  return (
    <Box paddingTop="12px" display="block">
      <CardInfo
        template={template}
        hovered={hovered}
        t={t}
        language={language}
        isMobileView={false}
        titleProps={{ weight: 'normal', size: 'medium' }}
        priceTextProps={{
          weight: 'normal',
          getLocalizedPrice: getLocalizedTotalPrice,
        }}
      />
    </Box>
  );
};

export default TemplateCardAdaptive;
